/**
 * JobSearchPortal.jsx — /job-search
 * AI Job Search Copilot — paid (ai_tools tier)
 * Tools: Cover Letter, Resume Summary, Job Matcher,
 *        Recruiter Reply, LinkedIn Post, Email Generator
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { TierGate, UpgradePrompt } from '../ProtectedRoute';
import ResumeManager from '../../components/ResumeManager';

// Import existing AI tool components
import {
  CoverLetterGenerator,
  ResumeSummaryGenerator,
  EmailGenerator,
  LinkedInGenerator,
} from '../AITools';  // adjust import path to match your project
import { RecruiterReplyGenerator, JobDescriptionMatcher } from '../NewAITools';

const T = {
  bg:'#07080c', bg2:'#0c0f18', bg3:'#111520', surf:'#161c2a',
  border:'rgba(255,255,255,.07)', borderH:'rgba(255,255,255,.13)',
  white:'#dce6fa', muted:'rgba(255,255,255,0.75)', dim:'rgba(255,255,255,0.75)',
  cyan:'#00c8f0', blue:'#4d8fff', green:'#2ecc71', red:'#e74c3c',
  sans:'"Syne","DM Sans",system-ui,sans-serif',
};

const TOOLS = [
  {
    id:        'job_matcher',
    label:     'Job Matcher',
    icon:      '🎯',
    desc:      'Match resume to job',
    component: JobDescriptionMatcher,
    badge:     'Start here',
    color:     T.cyan,
  },
  {
    id:        'cover_letter',
    label:     'Cover Letter',
    icon:      '📄',
    desc:      'Tailored cover letters',
    component: CoverLetterGenerator,
    badge:     null,
    color:     T.blue,
  },
  {
    id:        'resume_summary',
    label:     'Resume Summary',
    icon:      '📋',
    desc:      'ATS-friendly summaries',
    component: ResumeSummaryGenerator,
    badge:     null,
    color:     T.blue,
  },
  {
    id:        'recruiter_reply',
    label:     'Recruiter Reply',
    icon:      '🤝',
    desc:      'Reply to LinkedIn recruiters',
    component: RecruiterReplyGenerator,
    badge:     null,
    color:     T.cyan,
  },
  {
    id:        'linkedin_post',
    label:     'LinkedIn Post',
    icon:      '💼',
    desc:      'Posts that get noticed',
    component: LinkedInGenerator,
    badge:     null,
    color:     T.blue,
  },
  {
    id:        'email_gen',
    label:     'Email Generator',
    icon:      '📧',
    desc:      'Follow-ups and outreach',
    component: EmailGenerator,
    badge:     null,
    color:     T.cyan,
  },
];

export default function JobSearchPortal() {
  const [activeTool, setActiveTool] = useState('job_matcher');
  const { isAuthenticated } = useAuth();
  const [activeResume, setActiveResume] = useState(null);
  const active = TOOLS.find(t => t.id === activeTool);
  const ActiveComponent = active?.component;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing:border-box; margin:0; padding:0; }
      `}</style>

      <div style={{ background:T.bg, minHeight:'100vh', fontFamily:T.sans }}>

        {/* Header */}
        <div style={{
          background:T.bg2, borderBottom:`1px solid ${T.border}`,
          padding:'14px 24px', display:'flex', alignItems:'center',
          justifyContent:'space-between', position:'sticky', top:0, zIndex:10,
        }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <Link to="/" style={{ fontSize:16, fontWeight:900, color:T.white, textDecoration:'none', letterSpacing:'-.02em' }}>
              Tylent<span style={{ color:T.cyan }}>ed</span>
            </Link>
            <span style={{ color:T.dim }}>›</span>
            <div>
              <span style={{ fontSize:14, fontWeight:700, color:T.white }}>🎯 AI Job Search Copilot</span>
              <span style={{
                marginLeft:8, padding:'2px 8px', borderRadius:20,
                background:'rgba(0,200,240,.1)', border:'1px solid rgba(0,200,240,.25)',
                fontSize:10, fontWeight:700, color:T.cyan, verticalAlign:'middle',
              }}>AI Tools</span>
            </div>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <Link to="/pricing" style={{ fontSize:12.5, color:T.muted, textDecoration:'none' }}>Upgrade</Link>
            <Link to="/dashboard" style={{
              padding:'6px 14px', borderRadius:7, border:`1px solid ${T.border}`,
              background:T.surf, color:T.white, fontSize:12.5, textDecoration:'none',
            }}>Dashboard</Link>
          </div>
        </div>

        {/* Hero strip */}
        <div style={{
          padding:'28px 24px 20px',
          background:`linear-gradient(180deg, rgba(0,200,240,.04) 0%, transparent 100%)`,
          borderBottom:`1px solid ${T.border}`,
          textAlign:'center',
        }}>
          <h1 style={{ fontSize:'clamp(1.4rem,3vw,1.9rem)', fontWeight:900, color:T.white, letterSpacing:'-.02em', marginBottom:6 }}>
            🎯 AI Job Search Copilot
          </h1>
          <p style={{ fontSize:13.5, color:T.muted, maxWidth:520, margin:'0 auto' }}>
            Match your resume to any job · Generate cover letters · Optimise ATS keywords · Reply to recruiters
          </p>
        </div>

        <div style={{ display:'flex', maxWidth:1200, margin:'0 auto', padding:'0' }}>

          {/* Sidebar */}
          <div style={{
            width:220, flexShrink:0, borderRight:`1px solid ${T.border}`,
            background:T.bg2, minHeight:'calc(100vh - 130px)',
            padding:'16px 0',
          }}>
            <div style={{ padding:'0 12px', marginBottom:10, fontSize:10.5, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.1em' }}>
              Tools
            </div>
            {TOOLS.map(tool => (
              <button key={tool.id} onClick={() => setActiveTool(tool.id)} style={{
                width:'100%', padding:'10px 14px',
                display:'flex', alignItems:'center', gap:10,
                border:'none', background: activeTool===tool.id ? `rgba(0,200,240,.08)` : 'transparent',
                borderRight: activeTool===tool.id ? `2px solid ${T.cyan}` : '2px solid transparent',
                color: activeTool===tool.id ? T.white : T.muted,
                cursor:'pointer', textAlign:'left', transition:'all .12s',
              }}>
                <span style={{ fontSize:16 }}>{tool.icon}</span>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight: activeTool===tool.id ? 600 : 400 }}>{tool.label}</div>
                  <div style={{ fontSize:10.5, color:T.dim, marginTop:1 }}>{tool.desc}</div>
                </div>
                {tool.badge && (
                  <span style={{ fontSize:9, padding:'1px 6px', borderRadius:20, background:`${T.green}18`, border:`1px solid ${T.green}30`, color:T.green, whiteSpace:'nowrap' }}>
                    {tool.badge}
                  </span>
                )}
              </button>
            ))}

            {/* Switch portal links */}
            <div style={{ marginTop:24, padding:'16px 12px 0', borderTop:`1px solid ${T.border}` }}>
              <div style={{ fontSize:10.5, fontWeight:700, color:T.dim, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:10 }}>
                Other Portals
              </div>
              {[
                { label:'💼 Freelancer', path:'/freelancer' },
                { label:'⌨️ Developer',  path:'/developer'  },
                { label:'🛠️ Free Tools', path:'/tools'      },
                { label:'🍁 Finance',    path:'/finance'    },
              ].map(item => (
                <Link key={item.path} to={item.path} style={{
                  display:'block', padding:'6px 4px', fontSize:12.5, color:T.dim,
                  textDecoration:'none', transition:'color .12s',
                }}
                onMouseEnter={e=>e.currentTarget.style.color=T.muted}
                onMouseLeave={e=>e.currentTarget.style.color=T.dim}
                >{item.label}</Link>
              ))}
            </div>
          </div>

          {/* Tool panel */}
          <div style={{ flex:1, padding:'24px', minHeight:'calc(100vh - 130px)', overflow:'auto' }}>
            <TierGate
              requires="ai_tools"
              fallback={
                <UpgradePrompt
                  feature="AI Job Search Copilot"
                  requiredTier="ai_tools"
                  description="Get cover letters, job matching, recruiter replies and more with a 7-day free trial."
                />
              }
            >
              {ActiveComponent && (activeTool === 'job_matcher' || activeTool === 'cover_letter' || activeTool === 'resume_summary' ) &&
                  <ResumeManager onResumeSelect={(resume) => {
                // resume.skills, resume.summary etc available
                // pre-fill into Job Matcher or Cover Letter
                setActiveResume(resume);
          }}/>
        }
          <br/>
              {ActiveComponent &&   <ActiveComponent resume={activeResume}/>}
            </TierGate>
           
          </div>
        </div>
      </div>
    </>
  );
}
